import type { ReactComponentController } from './ReactComponentController';

import angular from 'angular';

/**
 * This is a copy of https://github.com/xjpro/react-in-angularjs
 * (https://www.npmjs.com/package/react-in-angularjs)
 * With applied fixes from this issue:
 * https://github.com/xjpro/react-in-angularjs/issues/28
 */

// fixed version of react-in-angular angularizeDirective
// - the linker function is broken in 18.0.0, referencing $element instead of element
// - the $watchGroup function is broken, referencing the wrong root element
// - if there is a patch to this library, this file can be removed and usage replaced with the libs version
export function angularizeDirective(
  factory: () => Promise<React.ComponentType>,
  directiveName: string,
  angularApp: angular.IModule | angular.ICompileProvider,
  bindings: { [key: string]: any } = {},
) {
  if (typeof window === 'undefined' || typeof angularApp === 'undefined') {
    return;
  }

  (angularApp as angular.IModule).directive(directiveName, () => ({
    scope: bindings,
    replace: true,
    require: bindings.ngModel ? 'ngModel' : undefined,
    link(scope, element, attrs, ctrl) {
      let controller: ReactComponentController | null = null;
      let unmounted = false;

      Promise.all([import('./ReactComponentController'), factory()]).then(
        ([{ ReactComponentController }, Component]) => {
          if (unmounted) {
            return;
          }

          controller = new ReactComponentController({
            el: element[0],
            Component,
            scope,
            bindings,
            ngModel: ctrl as angular.INgModelController | undefined,
          });

          // First render - needed?
          controller.render();
        },
      );

      // Watch for any changes in bindings, then rerender
      const keys: string[] = [];

      for (const bindingKey of Object.keys(bindings)) {
        if (/^data[A-Z]/.test(bindingKey)) {
          console.warn(
            `"${bindingKey}" binding for ${directiveName} directive will be undefined because AngularJS ignores attributes starting with data-`,
          );
        }

        if (bindings[bindingKey] !== '&') {
          keys.push(bindingKey);
        }
      }

      scope.$watchGroup(keys, () => controller && controller.render());

      scope.$on('$destroy', () => {
        unmounted = true;

        controller && controller.unmount();
      });
    },
  }));
}

let angularRoot = document.body;

/**
 * Quick and dirty workaround to allow dynamically change angular root when rendering stories
 */
export function setAngularRoot(el: HTMLElement) {
  angularRoot = el;
}

export function getService<T = any>(serviceName: string): T {
  return angular.element(angularRoot).injector().get(serviceName);
}
